import './retailer-details.scss';
import Header from '../../../organisms/header/header.js';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import decodeToken from '../../../../utils/decode-token';
import apiClient from '../../../../utils/apiClient';
import { Alert, Snackbar } from '@mui/material';
import ModalUnassignStaff from '../../../organisms/role-admin/modal-unassign-staff/modal-unassign-staff';
import { ROLES } from '../../../../constants/constants';
import { isAdminRole, isPmaRole } from '../../../../utils/role-utils';
import BackButton from '../../../atoms/back-button';

function RetailerDetails() {
  const { t } = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;
  const { retailerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [areaCode, setAreaCode] = useState('');
  const [contactInfo, setContactInfo] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('');

  const [openModal, setOpenModal] = useState(false);
  const [staffData, setStaffData] = useState({});

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const fetchData = useCallback(async () => {
    const url = `${process.env.REACT_APP_API_URL}/retailer?retailerId=${retailerId}`;

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache',
      },
    };

    try {
      const response = await apiClient.get(url, config);

      if (response.status === 200) {
        setContactInfo(response.data);
        setAreaCode(response.data?.retailer?.area_code || '');
      }
    } catch (error) {
      if (error.response) {
        setSnackbarOpen(true);
        setSnackbarSeverity('error');
        setSnackbarMessage(t('common-internal-server-error'));
      }
    }
  }, [retailerId, token, t]);

  useEffect(() => {
    if (token && retailerId) {
      fetchData();
    }
  }, [fetchData, location.key, token, retailerId]);

  const handleOpenModal = (data) => {
    if (data && data.name) {
      setStaffData(data);
      setOpenModal(true);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      {token && (isAdminRole(role) || isPmaRole(role)) ? (
        <div className="App">
          <Header />
          <main>
            <section className="contact-info admin">
              <div className="inner">
                <BackButton
                  fallbackTo={isPmaRole(role) ? '/pma/main/retailers' : '/admin/main/retailer'}
                  context={contactInfo?.retailer?.name}
                />

                <h2>{t('retailer-info-title')}</h2>

                {contactInfo ? (
                  <>
                    <div className="retailer">
                      <dl>
                        <div>
                          <dt>{t('common-name')}</dt>
                          <dd>{contactInfo.retailer.name}</dd>
                        </div>
                        <div>
                          <dt>{t('common-country')}</dt>
                          <dd>{t(`countries-${contactInfo.retailer.area_code}`)}</dd>
                        </div>
                        <div>
                          <dt>{t('common-city')}</dt>
                          <dd>{contactInfo.retailer.city}</dd>
                        </div>
                        <div>
                          <dt>{t('common-retailer-code')}</dt>
                          <dd>{contactInfo.retailer.retailer_code}</dd>
                        </div>
                      </dl>

                      <div className="actions">
                        {role !== 'pma' && role !== 'pmaGM' && (
                          <>
                            <button
                              onClick={() => {
                                navigate(`/admin/edit-retailer/${retailerId}`, {
                                  state: {
                                    breadcrumbName: contactInfo.retailer.name,
                                    retailer: contactInfo.retailer,
                                  },
                                });
                              }}
                              className="btn secondary"
                            >
                              {t('hac-profile-pa-action-edit')}
                            </button>

                            <button
                              onClick={() =>
                                navigate(`/admin/delete-user`, {
                                  state: {
                                    user: contactInfo.retailer,
                                    id: retailerId,
                                    role: 'shop',
                                  },
                                })
                              }
                              className="btn text"
                            >
                              {t('common-delete')}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div>
                      <h5>{t('retailer-info-title-retailergm')}</h5>

                      {role !== 'pma' && role !== 'pmaGM' && (
                        <div className="actions">
                          {!contactInfo.retailer_gm.id && (
                            <button
                              onClick={() => {
                                navigate(`/admin/new-retailerGM/${retailerId}`, {
                                  state: {
                                    areaCode: areaCode,
                                    jlr_code: contactInfo.retailer.retailer_code,
                                  },
                                });
                              }}
                              className="btn secondary"
                            >
                              <span className="icon-plus"></span>{' '}
                              {t('admin-new-retailerGM-action')}
                            </button>
                          )}
                        </div>
                      )}

                      {contactInfo.retailer_gm.name && (
                        <div className="person">
                          <dl>
                            <div>
                              <dt>{t('common-name')}</dt>
                              <dd>
                                {contactInfo.retailer_gm.name} {contactInfo.retailer_gm.last_name}
                              </dd>
                            </div>
                            <div>
                              <dt>{t('common-email')}</dt>
                              <dd>{contactInfo.retailer_gm.email}</dd>
                            </div>
                            <div>
                              <dt>{t('common-phone')}</dt>
                              <dd>
                                {contactInfo.retailer_gm.prefix_phone} {contactInfo.retailer_gm.phone}
                              </dd>
                            </div>
                          </dl>

                          {role !== 'pma' && role !== 'pmaGM' && (
                            <div className="actions">
                              <button
                                onClick={() =>
                                  navigate(`/admin/edit-retailerGM/${retailerId}`, {
                                    state: {
                                      areaCode: areaCode,
                                      breadcrumbName:
                                        contactInfo.retailer_gm.name +
                                        ' ' +
                                        contactInfo.retailer_gm.last_name,
                                      retailerGMEmail: contactInfo.retailer_gm.email,
                                    },
                                  })
                                }
                                className="btn secondary"
                              >
                                {t('retailer-info-action1')}
                              </button>

                              <button
                                onClick={() => handleOpenModal(contactInfo.retailer_gm)}
                                className="btn secondary"
                              >
                                {t('retailer-info-action4')}
                              </button>

                              <button
                                onClick={() =>
                                  navigate(`/admin/delete-user`, {
                                    state: {
                                      user: contactInfo.retailer_gm,
                                      role: ROLES.RetailerGM,
                                      retailer_id: retailerId,
                                      retailer_name: contactInfo.retailer.name,
                                    },
                                  })
                                }
                                className="btn text"
                              >
                                {t('admin-main-tab2-option3')}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <br />
                    <br />
                    <br />

                    <h5>{t('retailer-info-title-saleperson1')}</h5>

                    {role !== 'pma' && role !== 'pmaGM' && (
                      <div className="actions">
                        <button
                          onClick={() =>
                            navigate(`/admin/new-saleperson/${retailerId}`, {
                              state: {
                                areaCode: areaCode,
                                jlr_code: contactInfo.retailer.retailer_code,
                                retailerName: contactInfo.retailer.name,
                              },
                            })
                          }
                          className="btn secondary"
                        >
                          <span className="icon-plus"></span>{' '}
                          {t('admin-new-saleperson-action')}
                        </button>
                      </div>
                    )}

                    {contactInfo.sales_person && contactInfo.sales_person.length > 0 && (
                      <>
                        {contactInfo.sales_person.map((person) => (
                          <div className="person" key={person.id}>
                            <dl>
                              <div>
                                <dt>{t('common-name')}</dt>
                                <dd>{`${person.name} ${person.last_name}`}</dd>
                              </div>
                              <div>
                                <dt>{t('common-email')}</dt>
                                <dd>{person.email}</dd>
                              </div>
                              <div>
                                <dt>{t('common-phone')}</dt>
                                <dd>{`${person.prefix_phone} ${person.phone}`}</dd>
                              </div>
                            </dl>

                            {role !== 'pma' && role !== 'pmaGM' && (
                              <div className="actions">
                                <button
                                  onClick={() =>
                                    navigate(`/admin/edit-saleperson/${retailerId}`, {
                                      state: {
                                        user: person,
                                        role: ROLES.Retailer,
                                        retailer_id: retailerId,
                                        retailer_name: contactInfo.retailer.name,
                                      },
                                    })
                                  }
                                  className="btn secondary"
                                >
                                  {t('retailer-info-action1')}
                                </button>

                                <button
                                  onClick={() => handleOpenModal(person)}
                                  className="btn secondary"
                                >
                                  {t('retailer-info-action4')}
                                </button>

                                <button
                                  onClick={() =>
                                    navigate(`/admin/delete-user`, {
                                      state: {
                                        user: person,
                                        role: ROLES.Retailer,
                                        retailer_id: retailerId,
                                        retailer_name: contactInfo.retailer.name,
                                      },
                                    })
                                  }
                                  className="btn text"
                                >
                                  {t('admin-main-tab2-option3')}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </>
                ) : (
                  <div>Loading...</div>
                )}
              </div>
            </section>
          </main>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
          >
            <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>

          <ModalUnassignStaff
            open={openModal}
            onClose={handleCloseModal}
            staffData={staffData}
            retailerId={retailerId}
          />
        </div>
      ) : (
        <Navigate to="/login" />
      )}
    </>
  );
}

export default RetailerDetails;